/**
 * Base-path helpers for the Scores experience.
 *
 * The Scores UI is mounted in two places:
 *   1. Standalone build (`REACT_APP_VARIANT=scores` or `?variant=scores`)
 *      — lives at the root, e.g. `/`, `/news`, `/match/:id`.
 *   2. Nested inside the main SmartKick app at `/live-scores/*`.
 *
 * Components inside `/scores/` must NOT hardcode either prefix. They
 * call `useScoresPath('/news')` → returns `/news` (standalone) OR
 * `/live-scores/news` (nested). Same for navigate().
 */
import { createContext, useContext, useCallback } from 'react';
import { useNavigate as useRouterNavigate } from 'react-router-dom';

const ScoresBasePathContext = createContext('');

export const ScoresBasePathProvider = ScoresBasePathContext.Provider;

const join = (base, path) => {
  if (!base) return path === '' ? '/' : (path.startsWith('/') ? path : `/${path}`);
  if (path === '' || path === '/') return base;
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
};

export const useScoresBasePath = () => useContext(ScoresBasePathContext);

export const useScoresPath = (path = '') => {
  const base = useContext(ScoresBasePathContext);
  return join(base, path);
};

export const useScoresNavigate = () => {
  const navigate = useRouterNavigate();
  const base = useContext(ScoresBasePathContext);
  return useCallback((to, opts) => {
    if (typeof to === 'number') return navigate(to);  // back/forward
    return navigate(join(base, to), opts);
  }, [navigate, base]);
};
